import { useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Modal variants that live in the dashboard URL and therefore participate in
 * browser history. Transient confirm dialogs (SnapshotSetup/MigrationSetup)
 * stay on `useModalState` and are intentionally not routed here.
 */
export type RoutedModalVariant = 'view' | 'attention' | 'delete';

const MODAL_PARAM = 'modal';
const JOB_ID_PARAM = 'jobId';

const isRoutedModalVariant = (value: string | null): value is RoutedModalVariant =>
  value === 'view' || value === 'attention' || value === 'delete';

export interface ModalRouting {
  /** The modal variant encoded in the URL, or null when no modal is requested. */
  modalVariant: RoutedModalVariant | null;
  /** The job id encoded in the URL, or null for non-job modals. */
  modalJobId: string | null;
  /** Open a modal by pushing a new history entry (so browser back closes it). */
  openModal: (variant: RoutedModalVariant, jobId?: string) => void;
  /** Close the modal: pop history when we pushed it, else strip the params. */
  closeModal: () => void;
}

/**
 * Reads and writes dashboard modal open-state from the URL query string,
 * turning each modal into a real browser-history entry.
 *
 * URL shape: `/dashboard?modal=view|attention|delete&jobId=<id>`
 * (non-job modals may use `?modal=<variant>` without `jobId`).
 */
export const useModalRouting = (): ModalRouting => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { key: locationKey } = useLocation();

  const rawVariant = searchParams.get(MODAL_PARAM);
  const modalVariant = isRoutedModalVariant(rawVariant) ? rawVariant : null;
  const modalJobId = searchParams.get(JOB_ID_PARAM);

  const openModal = useCallback(
    (variant: RoutedModalVariant, jobId?: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(MODAL_PARAM, variant);
          if (jobId) {
            next.set(JOB_ID_PARAM, jobId);
          } else {
            next.delete(JOB_ID_PARAM);
          }
          return next;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );

  const closeModal = useCallback(() => {
    // When the modal param exists and this isn't the first in-app entry, this
    // render came from an openModal push — go back so the URL, scroll, and any
    // other page state are restored exactly. (`location.key` is used instead of
    // `window.history.state.idx` because the auth callback wipes that index.)
    if (searchParams.has(MODAL_PARAM) && locationKey !== 'default') {
      navigate(-1);
      return;
    }
    // Fallback (deep-link/refresh onto a modal URL): strip the params in place
    // so closing never navigates the user out of the app.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(MODAL_PARAM);
        next.delete(JOB_ID_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [navigate, searchParams, setSearchParams, locationKey]);

  return { modalVariant, modalJobId, openModal, closeModal };
};

export default useModalRouting;
